import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import {
  pushIntegrationToSheetMutation,
  updateIntegrationMutation,
} from "@/app/Settings/Settings/GraphQL/integrationMutations";
import { IntegrationRecord } from "@/app/Settings/Settings/GraphQL/integrationQueries";
import { Button } from "@/components/catalyst/button";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { toast } from "sonner";

function extractSpreadsheetId(input: string): string {
  const trimmed = input.trim();
  const match = trimmed.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  return match ? match[1] : trimmed;
}

export function GoogleSheetsIntegrationScreen({
  integration,
}: {
  integration: IntegrationRecord;
}) {
  const { mutateAsync } = useGqlMutation(updateIntegrationMutation);
  const { mutateAsync: pushToSheet, isPending: isPushing } = useGqlMutation(
    pushIntegrationToSheetMutation,
  );
  const metadata = integration?.metadata;
  const importUrl = metadata?.import_key
    ? new URL(
        `/api/integrations/google-sheets/import/${metadata.import_key}`,
        import.meta.env.VITE_API_URL,
      ).toString()
    : "";

  return (
    <EditableRowsProvider>
      <div className="space-y-6">
        {metadata?.last_error ? (
          <WarningBanner>
            <strong>Last import failed:</strong> {metadata.last_error}
            {metadata.last_error_at ? (
              <span className="ml-2 opacity-70">
                ({metadata.last_error_at})
              </span>
            ) : null}
          </WarningBanner>
        ) : null}
        <div className="rounded-md border p-4 text-sm">
          <h3 className="mb-2 font-medium">Setup instructions</h3>
          <ol className="list-decimal space-y-1 pl-5">
            <li>
              Open{" "}
              <a
                className="text-primary underline"
                href="https://docs.google.com/spreadsheets/d/1ujlDcmpm2hkyNj8i_--vIZJiMpyC9T3YHolLurGIKos/edit?usp=sharing"
                target="_blank"
                rel="noreferrer"
              >
                the template sheet
              </a>{" "}
              and choose <strong>File → Make a copy</strong>.
            </li>
            <li>
              In the copy, share it with{" "}
              <code>imoova@imoova-194802.iam.gserviceaccount.com</code> as an
              Editor (or set &quot;Anyone with the link&quot; to Editor).
            </li>
            <li>
              Select <code>A1:P1000</code> and choose{" "}
              <strong>Data → Create filter</strong>.
            </li>
            <li>Paste the sheet URL into the config below.</li>
          </ol>
        </div>
        <SettingRowsCard
          title="Spreadsheet"
          description="Configure the Google Sheet used to import relocations for this supplier."
        >
          <EditableSettingRow
            label="Spreadsheet"
            value={
              metadata?.sheet_name ||
              (metadata?.spreadsheet_id ? "Configured" : "")
            }
            editClassName="max-w-2xl"
            initialValues={{
              spreadsheet_id: metadata?.spreadsheet_id ?? "",
              sheet_name: metadata?.sheet_name ?? "",
            }}
            onSave={(values) =>
              mutateAsync({
                id: integration.id,
                input: {
                  metadata: {
                    ...metadata,
                    spreadsheet_id: extractSpreadsheetId(values.spreadsheet_id),
                    sheet_name: values.sheet_name.trim(),
                  },
                },
              })
            }
          >
            <TextInput
              name="spreadsheet_id"
              label="Spreadsheet ID or URL"
              className="col-span-12"
              inputProps={{
                placeholder:
                  "1yaFH1SlBg-aNr50YyHFQwMXBhvRie8teYePuNPCF2Hs or full sheet URL",
              }}
            />
            <TextInput
              name="sheet_name"
              label="Sheet tab name"
              className="col-span-12"
              inputProps={{ placeholder: "Sheet1 (leave blank for first tab)" }}
            />
          </EditableSettingRow>
        </SettingRowsCard>
        {metadata?.spreadsheet_id ? (
          <div className="space-y-2">
            <Button
              type="button"
              disabled={isPushing}
              onClick={async () => {
                try {
                  await pushToSheet({ id: integration.id });
                  toast.success("Sheet populated");
                } catch (e) {
                  toast.error(
                    e instanceof Error ? e.message : "Failed to populate sheet",
                  );
                }
              }}
            >
              {isPushing ? "Populating…" : "Populate sheet"}
            </Button>
            <p className="text-muted-foreground text-xs">
              Writes all active relocations into the sheet and refreshes the
              vehicle / office code dropdowns.
            </p>
          </div>
        ) : null}
        {metadata?.import_key ? (
          <p className="text-muted-foreground text-sm">
            {`We'll pull from the spreadsheet every hour. Push deals in now: `}
            <a
              className="text-primary break-all underline"
              href={importUrl}
              target="_blank"
              rel="noreferrer"
            >
              {importUrl}
            </a>
          </p>
        ) : null}
      </div>
    </EditableRowsProvider>
  );
}
